import { type FC } from 'react'
import { useFormContext } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { LoadingButton } from 'base-ui'

import {
  directContactFormBuildErrorSubjectLine,
  DirectContactFormErrorContent,
  DirectContactFormTextInfoSection,
  type useDirectContactFormSubmit,
} from '../../../direct-contact'
import { FriendlyCaptchaChallenge } from '../../../friendly-captcha'
import { type DirectContactFormLoggedOutNotificationType } from '../../model/directContactFormAccessorGetLoggedOutNotification'

import { DirectContactFormFields } from './DirectContactFormFields'
import { DirectContactFormImageUploadField } from './DirectContactFormImageUploadField'
import { type DirectContactFormValues } from './DirectContactFormModel'
import { DirectContactFormSubmitNotification } from './DirectContactFormSubmitNotification'

type DirectContactFormSubmit = ReturnType<typeof useDirectContactFormSubmit>

export const DirectContactFormContent: FC<{
  isCustomerDataComplete: boolean
  isLoggedIn: boolean
  isSubmitDisabled: boolean
  loggedOutNotificationType?: DirectContactFormLoggedOutNotificationType
  onCaptchaDone: (solution: string) => void
  shouldRenderCaptcha: boolean
  submit: DirectContactFormSubmit
  title: string
}> = ({
  isCustomerDataComplete,
  isLoggedIn,
  isSubmitDisabled,
  loggedOutNotificationType,
  onCaptchaDone,
  shouldRenderCaptcha,
  submit,
  title,
}) => {
  const { getValues, handleSubmit } = useFormContext<DirectContactFormValues>()
  const { isMutating, onSubmit, submitStatus } = submit

  if (submitStatus === 'success') {
    return <DirectContactFormSubmitNotification submitStatus={submitStatus} />
  }

  if (submitStatus === 'error') {
    const { orderId, phoneNumber, productCode, subject } = getValues()
    const subjectLine = directContactFormBuildErrorSubjectLine({
      orderId,
      phoneNumber,
      productCode,
      subject,
    })

    return <DirectContactFormErrorContent subjectLine={subjectLine} />
  }

  return (
    <>
      <h1 className="mb-4 text-2xl font-medium">{title}</h1>
      <DirectContactFormSubmitNotification submitStatus={submitStatus} />
      <form
        className="flex flex-col gap-4"
        data-qa-id="direct-contact-page-form"
        noValidate
        onSubmit={(event) => {
          if (event.target !== event.currentTarget) {
            return
          }
          event.preventDefault()
          void handleSubmit(onSubmit)(event)
        }}
      >
        <p className="text-xs text-dark-primary-strong">
          <FormattedMessage id="general.form.label.mandatoryFieldIndicator" />
        </p>
        <DirectContactFormFields
          isCustomerDataComplete={isCustomerDataComplete}
          isLoggedIn={isLoggedIn}
          loggedOutNotificationType={loggedOutNotificationType}
        />
        <DirectContactFormImageUploadField isLoggedIn={isLoggedIn} />
        <DirectContactFormTextInfoSection />
        {shouldRenderCaptcha ? (
          <FriendlyCaptchaChallenge
            doneCallback={onCaptchaDone}
            startMode="auto"
            whenThisPropChangesResetCaptcha={submitStatus !== 'idle'}
          />
        ) : null}
        <LoadingButton
          className="w-full"
          data-qa-id="contact-form-submit-button"
          disabled={isSubmitDisabled}
          loading={isMutating}
          size="large"
          type="submit"
        >
          <FormattedMessage id="product.consultation.button.submitMessage" />
        </LoadingButton>
      </form>
    </>
  )
}
